import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { consumePkce } from '../utils/pkce.js'
import { emailFromToken, isAdminFromToken } from '../utils/jwt.js'
import { bitolApiBase, setAuth } from '../lib/auth.js'
import { useAuthConfig } from '../config/appConfig.jsx'
import ErrorAlert from '../components/ErrorAlert.jsx'
import { PAGE_FORM } from '../lib/layout.js'
import { BTN_PRIMARY_BLOCK } from '../lib/ui.js'

// OAuth 2.1 authorization-code callback. Reads `code` + `state` from the
// redirect query, retrieves the persisted PKCE verifier, exchanges at
// /oauth2/token, persists the resulting tokens into auth state, then
// navigates to the original returnTo (or /admin by default).
//
// Ported from Workbench origin/main@60e779e:src/pages/OAuthCallbackPage.jsx.
// Same load-bearing behaviors:
//  - emailFromToken() result is REQUIRED — surface an error if neither
//    `email` nor `sub` is present (do not silently bounce the user to /).
//  - tokenExpiry is stored as ABSOLUTE ms (Date.now() + expires_in * 1000).
//    Mis-naming this silently disables useTokenRefresh — Workbench v1.2.5.
//  - Token exchange POSTs through the same-origin `/bitol` proxy:
//    `${bitolApiBase()}/oauth2/token`. The api-vm nginx `location /bitol/`
//    block proxy_pass'es to `http://127.0.0.1:8080/` (trailing slash), so
//    `/bitol/oauth2/token` is prefix-stripped to `127.0.0.1:8080/oauth2/token`
//    — Bitol's AS token endpoint — with no nginx change. Same-origin dodges
//    the AS's missing CORS on POST /oauth2/token (Workbench PR #93), and the
//    `/bitol` prefix mirrors how Workbench reaches the AS through its own
//    `/api` proxy. Dev: set VITE_BITOL_API_URL=http://localhost:8080.

export default function OAuthCallbackPage() {
  const navigate = useNavigate()
  const authConfig = useAuthConfig()
  const [error, setError] = useState(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const state = params.get('state')
    const oauthError = params.get('error')

    if (oauthError) {
      setError({
        title: 'Sign-in failed',
        message: params.get('error_description') || oauthError,
      })
      return
    }

    if (!code || !state) {
      setError({
        title: 'Sign-in failed',
        message: 'Missing authorization code or state. Please try signing in again.',
      })
      return
    }

    const { verifier, returnTo } = consumePkce(state)
    if (!verifier) {
      setError({
        title: 'Sign-in failed',
        message: 'The sign-in state was lost — likely a stale or replayed callback. Please try again.',
      })
      return
    }

    const clientId = authConfig?.clientId || 'next-webapp'
    const redirectUri = `${window.location.origin}/oauth/callback`

    let cancelled = false
    ;(async () => {
      try {
        const body = new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: clientId,
          code,
          redirect_uri: redirectUri,
          code_verifier: verifier,
        })
        const response = await fetch(`${bitolApiBase()}/oauth2/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
        })
        if (!response.ok) {
          const text = await response.text().catch(() => '')
          throw new Error(`Token exchange failed (HTTP ${response.status}): ${text || 'no body'}`)
        }
        const data = await response.json()
        const accessToken = data.access_token
        const refreshToken = data.refresh_token || ''
        const expiresIn = data.expires_in || null
        if (!accessToken) throw new Error('Token endpoint returned no access_token')

        const email = emailFromToken(accessToken)
        if (!email) {
          throw new Error('Access token has no email/sub claim — cannot establish session.')
        }
        setAuth({
          email,
          accessToken,
          refreshToken,
          tokenExpiry: expiresIn ? Date.now() + expiresIn * 1000 : null,
          isAdmin: isAdminFromToken(accessToken),
          authMode: 'oauth',
        })
        if (cancelled) return
        navigate(returnTo || '/admin', { replace: true })
      } catch (e) {
        if (!cancelled) setError({ title: 'Sign-in failed', message: e.message })
      }
    })()

    return () => { cancelled = true }
  }, [authConfig, navigate])

  return (
    <main className={`${PAGE_FORM} pb-12 pt-12`}>
      <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200/60 sm:p-8">
        {error ? (
          <>
            <ErrorAlert title={error.title} message={error.message} source="OAuthCallbackPage" />
            <button
              type="button"
              onClick={() => navigate('/', { replace: true })}
              className={`${BTN_PRIMARY_BLOCK} mt-5`}
            >
              Back home
            </button>
          </>
        ) : (
          <p className="text-sm text-slate-600">Completing sign-in…</p>
        )}
      </div>
    </main>
  )
}
